<script lang="ts" setup>
import type { IconName } from '~ds/lib/icons';

import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { DialogDescription, DialogTitle } from 'reka-ui';

import type { SchoolsFeedbackCategory, SchoolsFeedbackSubmission } from '~/types/Schools/SchoolsFeedback';

withDefaults(
  defineProps<{
    submitting?: boolean;
  }>(),
  {
    submitting: false,
  },
);

const emit = defineEmits<{
  submit: [submission: SchoolsFeedbackSubmission];
}>();

const open = defineModel<boolean>('open', { default: false });

const COMMENT_MAX_LENGTH = 1000;
const COMMENT_MIN_LENGTH = 20;
const DEFAULT_CATEGORY: SchoolsFeedbackCategory = 'general_feedback';

const categoryOptions: { value: SchoolsFeedbackCategory; labelKey: string; icon: IconName }[] = [
  { value: 'general_feedback', labelKey: 'schools.feedback.category.generalFeedback', icon: ICONS.messageQuestion },
  { value: 'feature_suggestion', labelKey: 'schools.feedback.category.featureSuggestion', icon: ICONS.idea },
  { value: 'bug_report', labelKey: 'schools.feedback.category.bugReport', icon: ICONS.tvIssue },
  { value: 'lesson_request', labelKey: 'schools.feedback.category.lessonRequest', icon: ICONS.bookOpen },
];

const selectedCategory = ref<SchoolsFeedbackCategory>(DEFAULT_CATEGORY);
const comment = ref('');
const followUpConsent = ref(true);

const canSubmit = computed(() => {
  const length = comment.value.trim().length;
  const hasValidCategory = selectedCategory.value && categoryOptions.some(option => option.value === selectedCategory.value);
  return hasValidCategory && length >= COMMENT_MIN_LENGTH && length <= COMMENT_MAX_LENGTH;
});

const commentValidationError = computed<{ key: string; params: Record<string, number> } | null>(() => {
  const length = comment.value.trim().length;

  if (length > 0 && length < COMMENT_MIN_LENGTH) {
    return { key: 'schools.feedback.commentTooShort', params: { num: COMMENT_MIN_LENGTH } };
  }

  if (length > COMMENT_MAX_LENGTH) {
    return { key: 'schools.feedback.commentTooLong', params: { num: COMMENT_MAX_LENGTH } };
  }

  return null;
});

const resetForm = () => {
  selectedCategory.value = DEFAULT_CATEGORY;
  comment.value = '';
  followUpConsent.value = true;
};

// Start each session with a clean slate once the modal is dismissed.
watch(open, (isOpen) => {
  if (!isOpen) {
    resetForm();
  }
});

const onSubmit = () => {
  if (!canSubmit.value) {
    return;
  }

  emit('submit', {
    category: selectedCategory.value,
    comment: comment.value.trim(),
    followUpConsent: followUpConsent.value,
  });
};
</script>

<template>
  <KUICoreDialog
    v-model="open"
    has-overlay
    content-width="md"
    padding="none"
    data-testid="schools-feedback-modal"
    :has-overflow="false"
    :close-disabled="submitting"
    :prevent-close="submitting"
  >
    <div class="flex grow flex-col gap-5 bg-background p-4 text-left sm:gap-6 sm:p-6">
      <header class="flex flex-col pr-8">
        <DialogTitle as-child>
          <Heading
            size="3xl"
            as="h2"
            class="mb-4"
          >
            {{ $t('schools.feedback.title') }}
          </Heading>
        </DialogTitle>
        <Heading
          size="xl"
          as="h3"
          weight="medium"
          class="opacity-80 leading-none"
        >
          {{ $t('schools.feedback.subtitle') }}
        </Heading>
        <DialogDescription as-child>
          <Text
            size="xl"
            weight="medium"
            class="opacity-80"
          >
            {{ $t('schools.feedback.lede') }}
          </Text>
        </DialogDescription>
      </header>

      <section
        role="radiogroup"
        :aria-label="$t('schools.feedback.categoryLabel')"
        class="grid grid-cols-2 gap-2 sm:grid-cols-4"
      >
        <KUICoreClickable
          v-for="option in categoryOptions"
          :key="option.value"
          role="radio"
          :aria-label="$t(option.labelKey)"
          :aria-checked="selectedCategory === option.value"
          :disabled="submitting"
          :data-testid="`schools-feedback-category-${option.value}`"
          class="w-full"
          @click="selectedCategory = option.value"
        >
          <KUICoreCard
            omit-background
            elevation="sm"
            rounding="2xl"
            padding="sm"
            class="h-full border-2 border-transparent transition-colors"
            :class="selectedCategory === option.value
              ? 'border-secondary! bg-secondary-subtle'
              : 'bg-white hover:bg-accent/50'"
          >
            <div class="flex flex-col items-center gap-2 py-1 text-center">
              <Icon
                :name="option.icon"
                size="lg"
                class="text-foreground"
              />
              <Text
                size="md"
                weight="medium"
                class="max-w-min leading-tight"
              >
                {{ $t(option.labelKey) }}
              </Text>
            </div>
          </KUICoreCard>
        </KUICoreClickable>
      </section>

      <KUICoreInputTextarea
        v-model="comment"
        :label="$t('schools.feedback.commentLabel')"
        :placeholder="$t('schools.feedback.commentPlaceholder')"
        :max-length="COMMENT_MAX_LENGTH"
        :rows="4"
        :disabled="submitting"
        :error-messages="commentValidationError ? [$t(commentValidationError.key, commentValidationError.params)] : []"
        data-test-id="schools-feedback-comment"
      />

      <section class="flex items-center justify-between gap-4">
        <div class="flex flex-col gap-0.5">
          <Text size="md" weight="medium">
            {{ $t('schools.feedback.followUpLabel') }}
          </Text>
          <Text size="sm" class="text-muted-foreground">
            {{ $t('schools.feedback.followUpDescription') }}
          </Text>
        </div>
        <KUICoreSwitch
          v-model="followUpConsent"
          :disabled="submitting"
          :aria-label="$t('schools.feedback.followUpLabel')"
        />
      </section>

      <div class="flex flex-col items-center gap-3">
        <Button
          variant="secondary"
          size="lg"
          :loading="submitting"
          :disabled="!canSubmit"
          data-testid="schools-feedback-submit"
          type="button"
          @click="onSubmit"
        >
          {{ $t('schools.feedback.submit') }}
        </Button>
        <div class="flex items-center gap-1.5">
          <Icon
            :name="ICONS.lock"
            size="xs"
            class="text-muted-foreground"
          />
          <Text size="sm" class="text-muted-foreground">
            {{ $t('schools.feedback.privacyNote') }}
          </Text>
        </div>
      </div>
    </div>
  </KUICoreDialog>
</template>
